import { UserPreferences, WheelchairSpecs } from '../types';

const STORAGE_KEYS = {
  USER_PREFERENCES: 'route-planner-user-preferences',
  WHEELCHAIR_SPECS: 'route-planner-wheelchair-specs',
  ONBOARDING_COMPLETED: 'route-planner-onboarding-completed',
  USER_ID: 'route-planner-user-id',
} as const;

export const localStorageUtils = {
  // User preferences
  getUserPreferences: (): UserPreferences | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error loading user preferences from localStorage:', error);
      return null;
    }
  },

  saveUserPreferences: (preferences: UserPreferences): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.USER_PREFERENCES, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving user preferences to localStorage:', error);
    }
  },

  // Wheelchair specifications
  getWheelchairSpecs: (): WheelchairSpecs[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.WHEELCHAIR_SPECS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading wheelchair specs from localStorage:', error);
      return [];
    }
  },

  saveWheelchairSpecs: (specs: WheelchairSpecs[]): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.WHEELCHAIR_SPECS, JSON.stringify(specs));
    } catch (error) {
      console.error('Error saving wheelchair specs to localStorage:', error);
    }
  },

  // Onboarding status
  isOnboardingCompleted: (): boolean => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETED);
      return stored === 'true';
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      return false;
    }
  },

  setOnboardingCompleted: (): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
    } catch (error) {
      console.error('Error setting onboarding completed:', error);
    }
  },

  // Clear all data (for testing)
  clearAll: (): void => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  },

  // Reset onboarding to start over
  resetOnboarding: (): void => {
    try {
      const preferences = localStorageUtils.getUserPreferences();
      if (preferences) {
        const resetPreferences = {
          ...preferences,
          hasCompletedOnboarding: false,
          hasCompletedPreferenceOnboarding: false
        };
        localStorageUtils.saveUserPreferences(resetPreferences);
      }
      localStorage.removeItem(STORAGE_KEYS.ONBOARDING_COMPLETED);
    } catch (error) {
      console.error('Error resetting onboarding:', error);
    }
  },

  // User ID
  getUserId: (): number | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.USER_ID);
      return stored ? parseInt(stored, 10) : null;
    } catch (error) {
      console.error('Error loading user ID from localStorage:', error);
      return null;
    }
  },

  saveUserId: (userId: number): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.USER_ID, userId.toString());
    } catch (error) {
      console.error('Error saving user ID to localStorage:', error);
    }
  },
};
